import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { deactivateTruckClass, listTruckClasses, reactivateTruckClass, type TruckClass } from '../../../reference-data.api';

export type TruckClassesState = { status: 'loading' } | { status: 'loaded'; classes: TruckClass[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): TruckClassesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/**
 * The truck-classes catalogue and its row actions, which have no form of their
 * own, so their failures toast (CLAUDE.md → Web conventions). Create and edit
 * are on the class's own page (`useVehicleClass`).
 */
export function useTruckClasses() {
  const [state, setState] = useState<TruckClassesState>({ status: 'loading' });
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listTruckClasses()
      .then((classes) => {
        if (!cancelled) setState({ status: 'loaded', classes });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', classes: await listTruckClasses() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  const deactivate = (row: TruckClass) => rowAction(() => deactivateTruckClass(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: TruckClass) => rowAction(() => reactivateTruckClass(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, deactivate, reactivate };
}
